<template>
  <div>
    <b-row>
      <b-col md="12" lg="8">
        <b-form-group label-for="input-search-member">
          <template #label>
            <slot name="search-label" />
          </template>
          <b-input id="input-search-member" placeholder="Search in whole company" @input="onInput" />
        </b-form-group>
        <div class="mb-4">
          <b-row v-for="profile in searched" :key="profile.id" align-v="center" class="mb-2">
            <b-col cols="auto">
              <avatar :src="profile.photo" size="2rem" />
            </b-col>
            <b-col>
              {{userName(profile)}}
            </b-col>
            <b-col cols="auto">
              <b-button variant="link" @click="onAdd(profile)">Add</b-button>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="12" lg="8">
        <b-form-group label-for="input-current-members">
          <template #label>
            <slot name="current-label" />
          </template>
          <b-row v-for="member in currentMembers" :key="member.profile.id" align-v="center" class="mb-2">
            <b-col cols="auto">
              <avatar :src="member.profile.photo" size="2rem" />
            </b-col>
            <b-col>
              {{userName(member.profile)}}
              <template v-if="member.isNew">
                <b-badge variant="primary" class="ml-3">New</b-badge>
              </template>
            </b-col>
            <template v-if="canDelete(member)">
              <b-col cols="auto">
                <b-button variant="link" @click="onRemove(member.profile)">Remove</b-button>
              </b-col>
            </template>
          </b-row>
        </b-form-group>
      </b-col>
    </b-row>
    <slot name="bottom" v-bind="{ changes: getChanges(), isChanged, cancel }" />
  </div>
</template>

<script>
import Avatar from '@/components/common/Avatar'
import { userName, profileQuery } from '@/helpers/helpers'

export default {
  name: 'ManageMembers',

  props: {
    initialMembers: {
      type: Array,
      default: () => ([])
      // Example: [{ canDelete: false, profileId: 1 }, { profileId: 2 }]
    },

    profiles: {
      type: Object,
      required: true
    },

    keepLast: {
      type: Boolean,
      default: true
    }
  },

  watch: {
    initialMembers() {
      this.cancel()
    },

    profiles() {
      this.cancel()
    }
  },

  computed: {
    currentMembers() {
      const result = []
      this.initialMembers.filter(member => {
        if (this.removed.indexOf(member.profileId) === -1) {
          result.push({ ...member, profile: this.profiles[member.profileId] })
        }
      })

      this.added.forEach(profileId => {
        result.unshift({ isNew: true, profile: this.profiles[profileId] })
      })

      return result
    },

    searched() {
      if (this.query.length < 2) return []

      const result = {}
      const filtered = profileQuery(this.profiles, this.query)

      for (const key in filtered) {
        const profileId = Number(key)
        const profile = filtered[profileId]
        const memberIdx = this.currentMembers.findIndex(p => p.profile.id === profileId)
        if (memberIdx === -1) {
          result[profileId] = profile
        }
      }

      return Object.values(result)
    },

    isChanged() {
      return this.added.length > 0 || this.removed.length > 0
    }
  },

  data() {
    return {
      query: '',
      added: [],
      removed: []
    }
  },

  methods: {
    userName(profile) {
      return userName(profile)
    },

    cancel() {
      this.added = []
      this.removed = []
    },

    canDelete(member) {
      const canDelete = member.canDelete || member.isNew
      return this.keepLast ? canDelete && this.currentMembers.length > 1 : canDelete
    },

    onInput(input) {
      this.query = input
    },

    getChanges() {
      return {
        added: [...this.added],
        removed: [...this.removed]
      }
    },

    emitChange() {
      this.$emit('change', this.getChanges())
    },

    onAdd(profile) {
      const idx = this.removed.indexOf(profile.id)
      if (idx !== -1) {
        this.removed.splice(idx, 1)
      } else {
        this.added.push(profile.id)
      }
      this.emitChange()
    },

    onRemove(profile) {
      const idx = this.added.indexOf(profile.id)
      if (idx !== -1) {
        this.added.splice(idx, 1)
      } else {
        this.removed.push(profile.id)
      }
      this.emitChange()
    }
  },

  components: {
    Avatar
  }
}
</script>
